import { Logging } from '#common/logging';
import { InvalidArgumentError } from '#common/types';
import { createServerCryptoProvider, CryptoTuple } from '#crypto';
import { createAesCrypto } from '#crypto/node-forge';
import { TelemetryName } from '#telemetry/types';
import { CommonConfigKeyHelper } from './common-config-key-helper';
import { createScoppedSetting } from './scoped-setting-impl';
import { createSettingsBuilder } from './settings-builder-impl';
import { SettingEncryption, } from './types';
export const createInitializerConfigManager = async ({ metaStore, tuple, keyValueHelpers, configWasm, commonConfigKey, }) => {
    const authorizer = undefined;
    const configManager = new ConfigManagerImpl(metaStore, tuple, keyValueHelpers, configWasm, authorizer, commonConfigKey);
    await configManager.setUp();
    return configManager;
};
export const createConfigManager = async ({ metaStore, tuple, keyValueHelpers, configWasm, authorizer, decryptedWorkspaceConfigKey, }) => {
    const commonConfigKey = undefined;
    const configManager = new ConfigManagerImpl(metaStore, tuple, keyValueHelpers, configWasm, authorizer, commonConfigKey, decryptedWorkspaceConfigKey);
    await configManager.setUp();
    return configManager;
};
class ConfigManagerImpl {
    metaStore;
    tuple;
    keyValueHelpers;
    configWasm;
    authorizer;
    decryptedWorkspaceConfigKey;
    log = Logging.getLogger('ConfigManager');
    aesCrypto = createAesCrypto();
    serverCrypto;
    settingsBuilder;
    commonConfigKeyHelper;
    constructor(metaStore, tuple, keyValueHelpers, configWasm, authorizer, commonConfigKey, decryptedWorkspaceConfigKey) {
        this.metaStore = metaStore;
        this.tuple = tuple;
        this.keyValueHelpers = keyValueHelpers;
        this.configWasm = configWasm;
        this.authorizer = authorizer;
        this.decryptedWorkspaceConfigKey = decryptedWorkspaceConfigKey;
        this.settingsBuilder = createSettingsBuilder(this.tuple, this.configWasm);
        this.serverCrypto = new CryptoTuple(this.tuple, createServerCryptoProvider(this));
        this.commonConfigKeyHelper = new CommonConfigKeyHelper(this, commonConfigKey);
    }
    setUp() {
        return this.commonConfigKeyHelper.fetchCommonConfigKey();
    }
    getSettingsBuilder() {
        return this.settingsBuilder;
    }
    async serializeGlobalConfig(config) {
        const kvs = new Array();
        for (const setting of config) {
            const kv = await this.createKeyValue(setting);
            kvs.push(kv);
        }
        return kvs;
    }
    async set(config) {
        this.log.debug('Setting global settings.');
        const operation = TelemetryName.SET_CONFIG;
        const telemetryValue = this.keyValueHelpers.makeSetConfigTelemetryTuple(config);
        return await this.authorizer.executeWithAdminPrivileges(async () => {
            const kvs = await this.serializeGlobalConfig(config);
            await this.metaStore.commitKeyValuePairs(kvs, false);
        }, { operation, telemetryValue });
    }
    async unset(settings) {
        this.log.debug('Unset global settings');
        const operation = TelemetryName.UNSET_CONFIG;
        const telemetryValue = this.keyValueHelpers.makeUnsetConfigTelemetryTuple(settings);
        return await this.authorizer.executeWithAdminPrivileges(async () => {
            await this.configWasm.unsetSettings(settings);
        }, { operation, telemetryValue });
    }
    async get(settings) {
        this.log.debug(settings, 'Retrieving settings...');
        const rawResult = await this.configWasm.getEncryptedSettings(settings);
        this.log.debug(`Got ${rawResult.length} settings`);
        const result = await Promise.all(rawResult.map(async (s) => {
            const globalValue = s.globalValue
                ? await this.decryptValue(s.globalValue, s.details.encryption)
                : undefined;
            return createScoppedSetting(s.name.target, s.name.name, this.tuple, this.configWasm, globalValue, s.defValue);
        }));
        this.log.trace('Parsed settings');
        return result;
    }
    async createKeyValue(setting) {
        const valueBytes = setting.rawValue();
        const encryption = setting.details().encryption;
        const value = setting.isPreencrypted() ? valueBytes : await this.encryptValue(valueBytes, encryption);
        const keyBytes = this.keyValueHelpers.makeGlobalConfigKey(setting.fullName(), encryption);
        return { key: keyBytes, value };
    }
    async encryptValue(settingValue, encryption) {
        if (encryption === SettingEncryption.BOOTSTRAP) {
            return settingValue;
        }
        if (encryption === SettingEncryption.CLIENT_AND_SERVER) {
            return await this.encryptCommonSetting(settingValue);
        }
        if (encryption === SettingEncryption.SERVER_ENCRYPTED) {
            return await this.encryptServerSetting(settingValue);
        }
        if (encryption === SettingEncryption.CLIENT) {
            return this.encryptClientSetting(settingValue);
        }
        throw new InvalidArgumentError(`Invalid setting encryption ${encryption}`);
    }
    async decryptValue(settingValue, encryption) {
        if (encryption === SettingEncryption.BOOTSTRAP) {
            return settingValue;
        }
        if (encryption === SettingEncryption.CLIENT_AND_SERVER) {
            return await this.decryptCommonSetting(settingValue);
        }
        if (encryption === SettingEncryption.CLIENT) {
            return this.decryptClientSetting(settingValue);
        }
        throw new InvalidArgumentError(`Invalid setting encryption ${encryption}`);
    }
    async encryptCommonSetting(settingValue) {
        const encrypted = await this.commonConfigKeyHelper.encryptCommon(settingValue);
        return this.tuple.makeTupleFromStringArray([encrypted.value, encrypted.iv]);
    }
    async encryptServerSetting(settingValue) {
        return this.serverCrypto.encrypt(settingValue);
    }
    encryptClientSetting(settingValue) {
        const encrypted = this.aesCrypto.encrypt(settingValue, this.decryptedWorkspaceConfigKey);
        return this.tuple.makeTupleFromStringArray([encrypted.value, encrypted.iv]);
    }
    async decryptCommonSetting(settingValue) {
        return this.decryptSettingValue(settingValue, await this.commonConfigKeyHelper.getCommonKey());
    }
    decryptClientSetting(settingValue) {
        return this.decryptSettingValue(settingValue, this.decryptedWorkspaceConfigKey);
    }
    decryptSettingValue(settingValue, cryptoKey) {
        const value = this.tuple.getByteArrayFromStringTuple(settingValue, 0);
        const iv = this.tuple.getByteArrayFromStringTuple(settingValue, 1);
        const decrypted = this.aesCrypto.decrypt({ value, iv }, cryptoKey);
        return decrypted;
    }
}
